import PropTypes from "prop-types";
import { useMutation } from "@tanstack/react-query";
import { useWatch } from "react-hook-form";
import { Volume2 } from "lucide-react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import ACL_RELATIONSHIPS from "acl-relationships";
import withRoleCheck from "components/hocs/withRoleCheck";
import { RoundedAudioPlayer } from "components/RoundedAudioPlayer";
import TwilioPhoneNumberService from "services/TwilioPhoneNumber";

// The preview is synthesized with Amazon Polly, so Google voices and the
// legacy default voice cannot be previewed.
const POLLY_VOICE_PREFIX = "Polly.";

const PreviewPlayer = ({ holdMessage, language, voice, id }) => {
  const preview = useMutation({
    mutationFn: () =>
      TwilioPhoneNumberService.previewHoldMessage({
        hold_message: holdMessage,
        hold_message_language: language,
        hold_message_voice: voice,
      }),
    onError: error => toast.error(error.message),
  });

  if (preview.data) {
    return <RoundedAudioPlayer id={id} src={preview.data.url} />;
  }
  return (
    <Button
      type="button"
      variant="outline"
      size="xs"
      disabled={preview.isPending}
      onClick={() => preview.mutate()}
    >
      <Volume2 />
      Preview
    </Button>
  );
};

PreviewPlayer.propTypes = {
  holdMessage: PropTypes.string.isRequired,
  language: PropTypes.string.isRequired,
  voice: PropTypes.string.isRequired,
  id: PropTypes.string.isRequired,
};

const AuthorizedPreviewPlayer = withRoleCheck(PreviewPlayer, [
  ACL_RELATIONSHIPS.twilioPhoneNumber.previewHoldMessage,
]);

/**
 * Button that synthesizes the hold message and then plays it in a compact
 * player. Renders nothing for a message that cannot be previewed.
 */
const HoldMessagePreview = ({ holdMessage, language, voice, id }) => {
  const text = holdMessage?.trim();
  if (!text || !language || !voice?.startsWith(POLLY_VOICE_PREFIX)) {
    return null;
  }
  return (
    <AuthorizedPreviewPlayer
      // An edited message must not keep playing the audio of the old one.
      key={`${voice}|${language}|${text}`}
      holdMessage={text}
      language={language}
      voice={voice}
      id={id}
    />
  );
};

HoldMessagePreview.propTypes = {
  holdMessage: PropTypes.string,
  language: PropTypes.string,
  voice: PropTypes.string,
  id: PropTypes.string.isRequired,
};

/** Previews the unsaved hold message fields of the surrounding form. */
export const HoldMessageFormPreview = ({
  messageName,
  languageName,
  voiceName,
}) => {
  const [holdMessage, language, voice] = useWatch({
    name: [messageName, languageName, voiceName],
  });
  return (
    <HoldMessagePreview
      holdMessage={holdMessage}
      language={language}
      voice={voice}
      id={`hold-message-preview-${messageName}`}
    />
  );
};

HoldMessageFormPreview.propTypes = {
  messageName: PropTypes.string.isRequired,
  languageName: PropTypes.string.isRequired,
  voiceName: PropTypes.string.isRequired,
};

export default HoldMessagePreview;
